import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { BadgeDollarSign, Building2, CheckCircle2, Hourglass, Link2, Rss } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { EmptyState } from '@/components/EmptyState';
import { ErrorState } from '@/components/ErrorState';
import { PageHeader } from '@/components/PageHeader';
import ConnectGoogleAdsButton from '@/components/ConnectGoogleAdsButton';
import { useSelection } from '@/contexts/SelectionContext';
import { adsErrorKey } from '@/lib/adsConnectErrors';
import { formatDecimal, formatInteger, numberLocale } from '@/lib/language';
import {
  ADS_WINDOW_OPTIONS,
  formatMoney,
  useAdsAccountActions,
  useAdsAccounts,
  useAdsProductPerformance,
  useAdsRolloutAccountIds,
  type AdsAccount,
  type AdsProductRow,
} from '@/hooks/useAdsPerformance';
import { isRolloutProtectedError, selectLockFor, type RolloutKnowledge, type SelectLock } from '@/lib/adsSelectLock';

/**
 * Google-Ads-Lesepfad, Panel hinter der nicht verlinkten Route (Spec AP4).
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * WARUM DIESE SEITE NIRGENDWO VERLINKT IST (harte Grenze G1)
 * ═════════════════════════════════════════════════════════════════════════════
 *
 * Der Scope `auth/adwords` ist bei Google als "sensitive" eingestuft und noch
 * NICHT freigegeben. Googles eigene Oberflaeche dazu: "If your app is already
 * public, do not deploy unverified scopes to your production traffic. This will
 * disrupt your users and may consume your unverified user quota."
 *
 * Die Route `/internal/ads-performance` ist deshalb per URL erreichbar und
 * sonst nirgends: nicht in `AppSidebar.tsx`, nicht in `commandPaletteItems.ts`.
 * Gepinnt von `src/test/adsScopeIsolation.test.ts`.
 *
 * ⚠ KEIN `import.meta.env.DEV`-Gate. Das Demo-Video fuer Googles
 * Scope-Freigabe muss diesen Flow in der DEPLOYTEN App zeigen; ein DEV-Gate
 * macht ihn unfilmbar. Der Schutz ist, dass die Seite unverlinkt ist, nicht
 * dass sie abgeschaltet waere.
 *
 * Copy vorerst nur englisch (Spec F3), aber vollstaendig ueber den
 * i18n-Namespace `adsPerformance` -- die deutsche Fassung ist damit spaeter
 * eine Uebersetzung und kein Umbau.
 */

/** Die Zustaende, die diese Seite unterscheiden muss. Als Typ ausgeschrieben,
 *  weil "kein Konto" und "Konto ohne Daten" zwei voellig verschiedene Aussagen
 *  sind und beide leicht zu einer leeren Tabelle verkuemmern. */
type PanelState =
  | 'no-feed'
  | 'loading'
  | 'accounts-error'
  | 'not-connected'
  | 'choose-account'
  | 'performance';

/** Wie viele Zeilen ohne Klick sichtbar sind. Muster `PerformanceDashboard`. */
const VISIBLE_ROWS = 50;

function accountLabel(account: AdsAccount, t: (key: string, options?: Record<string, unknown>) => string): string {
  const name = account.descriptive_name?.trim();
  return name && name.length > 0 ? name : t('accounts.unnamed');
}

// ── Zustand 2: kein Ads-Konto verbunden ─────────────────────────────────────
/**
 * Der Einstieg, auf dem das Demo-Video oeffnet. Die Aufzaehlung sagt in
 * ganzen Saetzen, was das Verbinden freischaltet, dass der Zugriff rein
 * lesend ist und dass er wieder loesbar ist. Genau diese drei Punkte prueft
 * Googles Verifizierung, und ein Video, das sie nur im Consent-Bildschirm
 * zeigt, zeigt sie in Googles Worten statt in unseren.
 */
function ConnectPanel({ onConnected }: { onConnected: () => void }) {
  const { t } = useTranslation('adsPerformance');

  return (
    <Card data-testid="ads-connect-panel">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          <BadgeDollarSign className="h-4 w-4 text-primary" />
          {t('notConnected.title')}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">{t('notConnected.body')}</p>
        <ul className="list-disc space-y-1.5 pl-5 text-sm text-muted-foreground">
          <li>{t('notConnected.bulletRead')}</li>
          <li className="font-medium text-foreground">{t('notConnected.bulletReadOnly')}</li>
          <li>{t('notConnected.bulletNightly')}</li>
          <li>{t('notConnected.bulletDisconnect')}</li>
        </ul>
        <p className="text-xs text-muted-foreground">{t('notConnected.consentHint')}</p>
        <ConnectGoogleAdsButton onConnected={onConnected} />
      </CardContent>
    </Card>
  );
}

// ── Zustand 3: Kontoauswahl ─────────────────────────────────────────────────

interface AccountRowProps {
  account: AdsAccount;
  isBusy: boolean;
  onSelect: (account: AdsAccount) => void;
  onDisconnect: (account: AdsAccount) => void;
  /**
   * Anzeigename des Verwaltungskontos, ueber das dieses Konto erreichbar ist
   * (#181). Gesetzt nur fuer MCC-verwaltete Zeilen -- die Zeile erklaert dann
   * sichtbar, warum sie in der Liste steht, obwohl der Google-Login sie nicht
   * direkt erreicht.
   */
  viaManagerName?: string | null;
  /** Darf dieses Konto per Klick aktiviert werden (WP0/A8G-01)? */
  selectLock: SelectLock;
}

function AccountRow({ account, isBusy, onSelect, onDisconnect, viaManagerName, selectLock }: AccountRowProps) {
  const { t, i18n } = useTranslation('adsPerformance');
  const dateLocale = numberLocale(i18n.language);

  const lastPull = account.last_pull_at
    ? t('accounts.lastPull', { date: new Date(account.last_pull_at).toLocaleDateString(dateLocale) })
    : t('accounts.lastPullNever');

  return (
    <div className="rounded-lg border p-3 space-y-2">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0 space-y-1">
          <p className="text-sm font-medium break-words">{accountLabel(account, t)}</p>
          <p className="text-xs text-muted-foreground tabular-nums">
            {t('accounts.customerId', { id: account.ads_customer_id })}
            {' · '}
            {account.currency
              ? t('accounts.currency', { currency: account.currency })
              : t('accounts.currencyUnknown')}
          </p>
          {viaManagerName && (
            <p data-testid="ads-via-manager" className="text-xs text-muted-foreground flex items-center gap-1">
              <Building2 className="h-3 w-3 flex-shrink-0" />
              {t('accounts.viaManager', { name: viaManagerName })}
            </p>
          )}
          <p className="text-xs text-muted-foreground flex items-center gap-1">
            <Link2 className="h-3 w-3 flex-shrink-0" />
            {account.linked_merchant_center_id
              ? t('accounts.linkedMerchant')
              : t('accounts.notLinkedMerchant')}
          </p>
          <p className="text-xs text-muted-foreground">{lastPull}</p>
          {account.last_pull_error && (
            <p className="text-xs font-medium text-destructive-ink break-words">
              {t('accounts.lastPullError', { error: account.last_pull_error })}
            </p>
          )}
        </div>

        <div className="flex flex-col items-end gap-2 shrink-0">
          {account.is_active && (
            <span className="inline-flex items-center gap-1 text-xs font-medium text-success-ink">
              <CheckCircle2 className="h-3.5 w-3.5" />
              {t('accounts.activeBadge')}
            </span>
          )}

          {/* Verwaltungskonten stehen in der Liste, sind aber nicht waehlbar.
              Sichtbar begruendet und nicht in einem Tooltip versteckt: ein
              Knopf, der ohne erkennbaren Grund fehlt, liest sich als Fehler
              der App. Die Regel wird ausserdem serverseitig durchgesetzt --
              `set_active_google_ads_account` gibt fuer `is_manager` false
              zurueck, das hier ist die Erklaerung dazu, nicht der Schutz. */}
          {account.is_manager ? (
            <span className="inline-flex items-center gap-1 text-xs font-medium text-muted-foreground">
              <Building2 className="h-3.5 w-3.5" />
              {t('accounts.managerLabel')}
            </span>
          ) : account.is_active ? (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="outline" size="sm" disabled={isBusy}>
                  {t('accounts.disconnectCta')}
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>{t('accounts.disconnectTitle')}</AlertDialogTitle>
                  <AlertDialogDescription asChild>
                    <span className="block space-y-2 text-sm text-muted-foreground">
                      <span className="block">{t('accounts.disconnectBody')}</span>
                      {/* Praezision, die hier zaehlt: das Trennen loescht unsere
                          Credentials, es widerruft NICHT den Zugriff bei Google.
                          Wer das verwechselt, glaubt seinen Zugang entzogen zu
                          haben und hat ihn noch. */}
                      <span className="block font-medium text-foreground">
                        {t('accounts.disconnectRevokeNote')}
                      </span>
                    </span>
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>{t('accounts.cancel')}</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={() => onDisconnect(account)}
                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  >
                    {t('accounts.disconnectConfirm')}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          ) : selectLock.kind === 'free' ? (
            <Button size="sm" disabled={isBusy} onClick={() => onSelect(account)}>
              {isBusy ? t('accounts.selecting') : t('accounts.selectCta')}
            </Button>
          ) : selectLock.kind === 'confirm' ? (
            // WP0/A8G-01: die Wahl schaltet andere aktive Konten desselben
            // Google-Kontos ab. Erst nachfragen und sie beim Namen nennen.
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button size="sm" variant="outline" disabled={isBusy}>
                  {isBusy ? t('accounts.selecting') : t('accounts.selectCta')}
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>{t('accounts.switchTitle')}</AlertDialogTitle>
                  <AlertDialogDescription asChild>
                    <span className="block space-y-2 text-sm text-muted-foreground">
                      <span className="block">{t('accounts.switchBody')}</span>
                      <span data-testid={`ads-switch-siblings-${account.id}`} className="block font-medium text-foreground">
                        {selectLock.siblings.map((s) => accountLabel(s, t)).join(', ')}
                      </span>
                    </span>
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>{t('accounts.cancel')}</AlertDialogCancel>
                  <AlertDialogAction onClick={() => onSelect(account)}>{t('accounts.switchConfirm')}</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          ) : selectLock.kind === 'locked-checking' ? (
            // WP0/A8G-01: die Rollout-Pruefung laeuft noch. Gesperrt, aber ohne
            // Fehlertext: das ist kein Fehler, nur noch keine Antwort.
            <div className="flex max-w-[18rem] flex-col items-end gap-1">
              <Button size="sm" variant="outline" disabled>
                {t('accounts.selectCta')}
              </Button>
              <p data-testid={`ads-select-checking-${account.id}`} className="text-right text-xs text-muted-foreground">
                {t('accounts.lockedChecking')}
              </p>
            </div>
          ) : (
            // WP0/A8G-01: gesperrt, der Server sperrt ebenfalls
            // (ROLLOUT_SIBLING_PROTECTED). Gescheiterte Pruefung heisst gesperrt.
            <div className="flex max-w-[18rem] flex-col items-end gap-1">
              <Button size="sm" variant="outline" disabled>
                {t('accounts.selectCta')}
              </Button>
              <p data-testid={`ads-select-locked-${account.id}`} className="text-right text-xs text-muted-foreground">
                {selectLock.kind === 'locked-rollout'
                  ? t('accounts.lockedRollout', { accounts: selectLock.siblings.map((s) => accountLabel(s, t)).join(', ') })
                  : t('accounts.lockedUnknown')}
              </p>
            </div>
          )}
        </div>
      </div>

      {account.is_manager && (
        <p data-testid="ads-manager-reason" className="text-xs text-muted-foreground">
          {t('accounts.managerReason')}
        </p>
      )}
    </div>
  );
}

interface AccountsPanelProps {
  accounts: AdsAccount[];
  hasActiveAccount: boolean;
  isBusy: boolean;
  onSelect: (account: AdsAccount) => void;
  onDisconnect: (account: AdsAccount) => void;
  onConnected: () => void;
  rollout: RolloutKnowledge;
}

function AccountsPanel({
  accounts,
  hasActiveAccount,
  isBusy,
  onSelect,
  onDisconnect,
  onConnected,
  rollout,
}: AccountsPanelProps) {
  const { t } = useTranslation('adsPerformance');

  // Gruppierung (#181): Top-Level sind Konten ohne login_customer_id, in der
  // bisherigen Reihenfolge. Unter jeder MCC-Zeile stehen eingerueckt die von
  // ihr verwalteten Konten. Waisen -- verwaltet, aber ohne passende MCC-Zeile
  // in der Liste -- folgen danach flach: verschwinden duerfen sie nicht, denn
  // waehlbar sind sie trotzdem.
  const topLevel = accounts.filter((account) => !account.login_customer_id);
  const topLevelCustomerIds = new Set(topLevel.map((account) => account.ads_customer_id));
  const orphans = accounts.filter(
    (account) => account.login_customer_id && !topLevelCustomerIds.has(account.login_customer_id),
  );
  const managerName = (mcc: AdsAccount): string => {
    const name = mcc.descriptive_name?.trim();
    return name && name.length > 0 ? name : mcc.ads_customer_id;
  };

  return (
    <Card data-testid="ads-accounts-panel">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          <BadgeDollarSign className="h-4 w-4 text-primary" />
          {hasActiveAccount ? t('accounts.title') : t('accounts.chooseTitle')}
        </CardTitle>
        {!hasActiveAccount && (
          <p className="text-xs text-muted-foreground">{t('accounts.chooseBody')}</p>
        )}
      </CardHeader>
      <CardContent className="space-y-3">
        {topLevel.map((account) => {
          const children = accounts.filter(
            (child) => child.login_customer_id === account.ads_customer_id,
          );
          return (
            <div key={account.id} className="space-y-3">
              <AccountRow
                account={account}
                isBusy={isBusy}
                onSelect={onSelect}
                onDisconnect={onDisconnect}
                selectLock={selectLockFor(account, accounts, rollout)}
              />
              {children.length > 0 && (
                <div
                  data-testid={`ads-managed-children-${account.id}`}
                  className="ml-6 border-l pl-3 space-y-3"
                >
                  {children.map((child) => (
                    <AccountRow
                      key={child.id}
                      account={child}
                      isBusy={isBusy}
                      onSelect={onSelect}
                      onDisconnect={onDisconnect}
                      viaManagerName={managerName(account)}
                      selectLock={selectLockFor(child, accounts, rollout)}
                    />
                  ))}
                </div>
              )}
            </div>
          );
        })}
        {orphans.map((account) => (
          <AccountRow
            key={account.id}
            account={account}
            isBusy={isBusy}
            onSelect={onSelect}
            onDisconnect={onDisconnect}
            // Ohne MCC-Zeile in der Liste bleibt nur die Kundennummer als Name.
            viaManagerName={account.login_customer_id}
            selectLock={selectLockFor(account, accounts, rollout)}
          />
        ))}

        {/* Der Verbinden-Knopf bleibt erreichbar, auch wenn schon Zeilen
            existieren. Grund steht an `useAdsAccounts`: nach dem Trennen
            bleibt die Zeile stehen und die Token-Spalten sind fuer den Client
            nicht lesbar. "Wieder verbinden" ist also derselbe Weg wie
            "zum ersten Mal verbinden", und ohne diesen Knopf gaebe es aus dem
            getrennten Zustand keinen Rueckweg. */}
        <div className="space-y-2 border-t border-border pt-3">
          <ConnectGoogleAdsButton onConnected={onConnected} />
          <p className="text-xs text-muted-foreground">{t('accounts.connectHint')}</p>
        </div>
      </CardContent>
    </Card>
  );
}

// ── Zustand 4: verbunden, aber noch keine Daten ─────────────────────────────
/**
 * KEINE leere Tabelle. Eine Tabelle mit null Zeilen liest sich als "dieser
 * Feed gibt nichts aus", und das ist am Tag der Verbindung garantiert falsch:
 * der Cron laeuft um 06:30 UTC, ein heute verbundenes Konto hat vor morgen
 * frueh keine einzige Zeile.
 */
function NoDataPanel({ account, windowDays }: { account: AdsAccount; windowDays: number }) {
  const { t, i18n } = useTranslation('adsPerformance');
  const dateLocale = numberLocale(i18n.language);

  return (
    <Card data-testid="ads-no-data">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          <Hourglass className="h-4 w-4 text-primary" />
          {t('noData.title')}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        <p className="text-sm text-muted-foreground">{t('noData.body', { days: windowDays })}</p>
        <p className="text-sm text-muted-foreground">{t('noData.schedule')}</p>
        <p className="text-xs text-muted-foreground">{t('noData.matchHint')}</p>
        <p className="text-xs text-muted-foreground">
          {account.last_pull_at
            ? t('accounts.lastPull', {
                date: new Date(account.last_pull_at).toLocaleDateString(dateLocale),
              })
            : t('accounts.lastPullNever')}
        </p>
        {account.last_pull_error && (
          <p className="text-xs font-medium text-destructive-ink break-words">
            {t('accounts.lastPullError', { error: account.last_pull_error })}
          </p>
        )}
      </CardContent>
    </Card>
  );
}

// ── Zustand 5: die Tabelle ──────────────────────────────────────────────────

/** ROAS oder der Null-Platzhalter. `formatDecimal` liefert fuer `null` bereits
 *  den projektweiten Platzhalter, deshalb steht hier kein zweiter. */
function roasText(row: AdsProductRow, language: string | null | undefined): string {
  return formatDecimal(row.roas, language, 2);
}

interface AdsProductTableProps {
  rows: AdsProductRow[];
  currency: string | null;
  latestDate: string | null;
  windowDays: number;
  /** Gesamtzahl der Produkte mit Ads-Daten im Fenster (RPC `total_products`).
   *  Kann GROESSER sein als `rows.length`: die RPC kappt auf die teuersten
   *  1000 (PostgREST db-max-rows). Die Kopfzeile nennt DIESE Zahl -- alles
   *  andere waere eine falsche Aussage ueber den Feed des Kunden. */
  totalProducts: number;
}

function AdsProductTable({ rows, currency, latestDate, windowDays, totalProducts }: AdsProductTableProps) {
  const { t, i18n } = useTranslation('adsPerformance');
  const language = i18n.language;
  const dateLocale = numberLocale(language);
  const [showAll, setShowAll] = useState(false);

  const visible = showAll ? rows : rows.slice(0, VISIBLE_ROWS);

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2 flex-wrap">
          <BadgeDollarSign className="h-4 w-4 text-primary" />
          {t('table.title')}
          <span className="text-xs text-muted-foreground font-normal ml-auto flex items-center gap-3 flex-wrap">
            <span>{t('table.productCount', { count: totalProducts })}</span>
            {latestDate && (
              <span>
                {t('table.dataThrough', {
                  date: new Date(latestDate + 'T00:00:00').toLocaleDateString(dateLocale),
                })}
              </span>
            )}
            <span>{t('table.windowDays', { days: windowDays })}</span>
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-xs text-muted-foreground">
          {t('table.sortNote')} {t('table.roasHint')} {t('table.noCostNote')}
          {/* Die Kappung SICHTBAR machen statt still: die RPC liefert die
              teuersten 1000 (PostgREST db-max-rows), `totalProducts` zaehlt
              alle. Steht total ueber der Zeilenzahl, sagt die Tabelle dazu,
              was sie zeigt -- sonst laese sich die Liste als der ganze Feed. */}
          {totalProducts > rows.length && <> {t('table.topByCost', { count: rows.length })}</>}
        </p>

        {/* Sieben Spalten passen unter 768px CSS-Breite nur per Querscroll,
            und genau diese Ansicht wird im Demo-Video aufgenommen. Muster
            `PerformanceDashboard.tsx:363-381`: ab md die Tabelle, darunter
            dieselben Werte als Karten. `overflow-x-auto` bleibt trotzdem
            stehen, damit ein schmales Desktopfenster scrollt statt die Seite
            zu sprengen. */}
        <div data-testid="ads-product-table" className="hidden md:block overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="min-w-[220px]">{t('table.product')}</TableHead>
                <TableHead className="text-right">{t('table.impressions')}</TableHead>
                <TableHead className="text-right">{t('table.clicks')}</TableHead>
                <TableHead className="text-right">{t('table.cost')}</TableHead>
                <TableHead className="text-right">{t('table.conversions')}</TableHead>
                <TableHead className="text-right">{t('table.conversionsValue')}</TableHead>
                <TableHead className="text-right">{t('table.roas')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {visible.map((row) => (
                <TableRow key={row.product_id}>
                  <TableCell
                    data-testid="ads-product-title"
                    className="font-medium text-sm max-w-[320px] truncate"
                    title={row.title ?? undefined}
                  >
                    {row.title || '—'}
                  </TableCell>
                  <TableCell className="text-right text-sm tabular-nums">
                    {formatInteger(row.impressions, language)}
                  </TableCell>
                  <TableCell className="text-right text-sm tabular-nums">
                    {formatInteger(row.clicks, language)}
                  </TableCell>
                  <TableCell className="text-right text-sm tabular-nums">
                    {formatMoney(row.cost, currency, language)}
                  </TableCell>
                  <TableCell className="text-right text-sm tabular-nums">
                    {formatDecimal(row.conversions, language, 2)}
                  </TableCell>
                  <TableCell className="text-right text-sm tabular-nums">
                    {formatMoney(row.conversionsValue, currency, language)}
                  </TableCell>
                  <TableCell
                    data-testid="ads-product-roas"
                    className="text-right text-sm font-medium tabular-nums"
                    title={row.roas === null ? t('table.noCostNote') : undefined}
                  >
                    {roasText(row, language)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        <div className="md:hidden space-y-3">
          {visible.map((row) => (
            <div key={`card-${row.product_id}`} className="rounded-lg border p-3 space-y-2">
              <div className="flex items-start justify-between gap-2">
                <p className="text-sm font-medium break-words" title={row.title ?? undefined}>
                  {row.title || '—'}
                </p>
                <span className="text-sm font-medium shrink-0 tabular-nums">
                  {t('table.roas')} {roasText(row, language)}
                </span>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="text-[10px] uppercase tracking-wide text-muted-foreground">
                    {t('table.cost')}
                  </p>
                  <p className="text-sm tabular-nums">{formatMoney(row.cost, currency, language)}</p>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-wide text-muted-foreground">
                    {t('table.conversionsValue')}
                  </p>
                  <p className="text-sm tabular-nums">
                    {formatMoney(row.conversionsValue, currency, language)}
                  </p>
                </div>
              </div>
              <p className="text-xs text-muted-foreground tabular-nums">
                {t('table.impressions')}: {formatInteger(row.impressions, language)}
                {' · '}
                {t('table.clicks')}: {formatInteger(row.clicks, language)}
                {' · '}
                {t('table.conversions')}: {formatDecimal(row.conversions, language, 2)}
              </p>
            </div>
          ))}
        </div>

        {rows.length > VISIBLE_ROWS && (
          <button
            type="button"
            onClick={() => setShowAll(!showAll)}
            className="text-sm text-primary hover:underline"
          >
            {showAll ? t('table.showLess') : t('table.showAll', { count: rows.length })}
          </button>
        )}
      </CardContent>
    </Card>
  );
}

// ── Seite ───────────────────────────────────────────────────────────────────

const AdsPerformance: React.FC = () => {
  const { t } = useTranslation('adsPerformance');
  const { selectedFeedId, feed } = useSelection();

  // 30 Tage als Vorgabe, nicht 14 wie auf der Wirkungs-Seite: ein frisch
  // verbundenes Konto hat wenige Tage Historie, und die Frage dieser Seite
  // ("was kostet dieser Feed") wird ueber einen Monat sinnvoller beantwortet
  // als ueber zwei Wochen.
  const [windowDays, setWindowDays] = useState<number>(30);

  const {
    data: accounts = [],
    isLoading: accountsLoading,
    isError: accountsError,
    refetch: refetchAccounts,
  } = useAdsAccounts();

  const activeAccount = accounts.find((account) => account.is_active) ?? null;

  const {
    data: performance,
    isLoading: performanceLoading,
    isError: performanceError,
    refetch: refetchPerformance,
  } = useAdsProductPerformance(selectedFeedId, activeAccount?.id ?? null, windowDays);

  const { activateAccount, isActivating, disconnectAccount, isDisconnecting, invalidateAdsQueries } =
    useAdsAccountActions();

  const rolloutQuery = useAdsRolloutAccountIds();
  // Laden und Fehler getrennt (Review-Nachtrag): nur ein echter Fehler zeigt
  // den Fehlertext, waehrend des Ladens steht ein neutraler Hinweis.
  const rollout: RolloutKnowledge = rolloutQuery.isSuccess
    ? rolloutQuery.data
    : rolloutQuery.isError
      ? 'error'
      : 'loading';

  // ── Rueckweg aus dem Vollbild-Consent (Popup blockiert) ────────────────────
  //
  // `GoogleCallback` kann das Ergebnis nur dann per `postMessage` zurueckgeben,
  // wenn ein `window.opener` existiert. War das Popup blockiert, lief der
  // Consent als Vollbild-Weiterleitung, und der Callback schickt den Nutzer
  // hierher zurueck: bei Erfolg auf den nackten Pfad, bei Fehlschlag mit
  // `?adsOauth=error&code=…`. Der Kommentar dort sagt woertlich, dass DIESES
  // Panel den Code aufloest, "damit dort nicht geraten werden muss".
  //
  // Ohne diesen Block endet ein blockiertes Popup vollkommen stumm: der Nutzer
  // landet auf einer Seite, die weiterhin "kein Konto verbunden" sagt, und
  // erfaehrt nie, woran es lag.
  const [searchParams, setSearchParams] = useSearchParams();
  const oauthParamHandled = useRef(false);

  useEffect(() => {
    if (oauthParamHandled.current) return;
    const outcome = searchParams.get('adsOauth');
    if (!outcome) return;
    // Einmal-Sperre gegen den doppelten Effektlauf im StrictMode: sonst steht
    // dieselbe Fehlermeldung zweimal uebereinander.
    oauthParamHandled.current = true;

    if (outcome === 'error') {
      toast.error(t('connect.errorTitle'), {
        description: t(adsErrorKey(searchParams.get('code'))),
      });
    }

    // Parameter entfernen, sonst wiederholt jeder Reload die Meldung zu einem
    // Vorgang, der laengst vorbei ist.
    const next = new URLSearchParams(searchParams);
    next.delete('adsOauth');
    next.delete('code');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams, t]);

  const handleSelect = async (account: AdsAccount) => {
    try {
      await activateAccount(account.id);
    } catch (error) {
      toast.error(isRolloutProtectedError(error) ? t('errors.rolloutProtected') : t('errors.activateFailed'));
    }
  };

  const handleDisconnect = async (account: AdsAccount) => {
    try {
      await disconnectAccount(account.id);
    } catch {
      toast.error(t('errors.disconnectFailed'));
    }
  };

  // Nach dem Verbinden (Popup-Rueckweg) sind Kontenliste, Leistungszahlen UND
  // die Teaser-Kontozeile des Feed-Checks (useLinkedAdsAccount, 30 min stale)
  // veraltet. Dieselbe Invalidierung wie nach Aktivieren/Trennen, ein Pfad.
  const handleConnected = () => invalidateAdsQueries();

  const state: PanelState = !selectedFeedId
    ? 'no-feed'
    : accountsLoading
      ? 'loading'
      : accountsError
        ? 'accounts-error'
        : accounts.length === 0
          ? 'not-connected'
          : activeAccount
            ? 'performance'
            : 'choose-account';

  const products = performance?.products ?? [];

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('title')}
        subtitle={selectedFeedId ? t('subtitle', { feed: feed?.display_name ?? '' }) : undefined}
        actions={
          state === 'performance' ? (
            <Select value={String(windowDays)} onValueChange={(value) => setWindowDays(Number(value))}>
              <SelectTrigger className="w-[160px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ADS_WINDOW_OPTIONS.map((days) => (
                  <SelectItem key={days} value={String(days)}>
                    {t('table.windowDays', { days })}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : undefined
        }
      />

      {state === 'no-feed' && (
        <EmptyState icon={Rss} title={t('empty.noFeed')} description={t('empty.noFeedDesc')} />
      )}

      {state === 'loading' && (
        <div className="space-y-4">
          <Skeleton className="h-32 w-full rounded-lg" />
          <Skeleton className="h-64 w-full rounded-lg" />
        </div>
      )}

      {/* Exklusiver Zweig, Muster `PerformanceDashboard`: ein Ladefehler ist
          KEINE Messung. Unter einem Fehlerbanner noch "kein Ads-Konto
          verbunden" zu behaupten waeren zwei sich widersprechende Aussagen im
          selben Bild, und die falsche davon betrifft das Konto des Kunden. */}
      {state === 'accounts-error' && (
        <ErrorState description={t('errors.accountsFailed')} onRetry={() => refetchAccounts()} />
      )}

      {state === 'not-connected' && <ConnectPanel onConnected={handleConnected} />}

      {(state === 'choose-account' || state === 'performance') && (
        <AccountsPanel
          accounts={accounts}
          hasActiveAccount={!!activeAccount}
          isBusy={isActivating || isDisconnecting}
          onSelect={handleSelect}
          onDisconnect={handleDisconnect}
          onConnected={handleConnected}
          rollout={rollout}
        />
      )}

      {state === 'performance' && activeAccount && (
        <>
          {performanceLoading ? (
            <div className="space-y-2">
              {Array.from({ length: 6 }).map((_, index) => (
                <Skeleton key={index} className="h-12 w-full rounded-md" />
              ))}
            </div>
          ) : performanceError ? (
            <ErrorState
              description={t('errors.performanceFailed')}
              onRetry={() => refetchPerformance()}
            />
          ) : products.length === 0 ? (
            <NoDataPanel account={activeAccount} windowDays={windowDays} />
          ) : (
            <AdsProductTable
              rows={products}
              currency={activeAccount.currency}
              latestDate={performance?.latestDate ?? null}
              windowDays={windowDays}
              totalProducts={performance?.totalProducts ?? products.length}
            />
          )}
        </>
      )}
    </div>
  );
};

export default AdsPerformance;
